/**
 * [Phase 2d Stripe - Session B, founder-locked redlines] Pricing page.
 *
 * Public marketing surface - three products, founder-locked prices:
 *
 *   • Auction Draft Pass - $34.99 / $39.99 / $44.99
 *   • PGF Draft Analytics - $4.99 per draft
 *   • In-Season PGF Analytics - $6.99 / mo (Pregame Fantasy First Year
 *     Launch Price)
 *
 * No season pass. No coupons. No "$X normally" strikethroughs.
 */
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";

const API = process.env.REACT_APP_BACKEND_URL;

function auctionBasePrice() {
  const now = new Date();
  const cutoff = new Date("2026-08-01T00:00:00Z");
  return now < cutoff ? "$34.99" : "$39.99";
}

const TIERS = [
  {
    key: "snake",
    title: "Snake Draft + Standard Season",
    price: "Free",
    tagline: "Forever. No card. No catch.",
    items: [
      "Snake draft engine with Adam announcer",
      "Live activity feed + league chat",
      "Full season + playoffs at no cost",
    ],
    cta: null,
  },
  {
    key: "auction_draft_pass",
    title: "Auction Draft Pass",
    getPrice: auctionBasePrice,
    priceNote: "Early bird through July 31 · $39.99 thereafter · $44.99 extended (10+ leagues)",
    tagline: "Commissioner once per season. Covers up to 10 leagues.",
    items: [
      "Real-time auction engine with anti-snipe",
      "Adam auctioneer voice on every nomination",
      "Upgrade to extended ($44.99) anytime - unlimited leagues",
    ],
    product: "auction_draft_pass",
    requiresLeague: true,
  },
  {
    key: "analytics_draft_access",
    title: "PGF Draft Analytics",
    price: "$4.99",
    priceNote: "Per draft · per user · auction or snake",
    tagline: "Unlock the analytics cockpit for one draft.",
    items: [
      "Five-axis PGF Rank radar",
      "Bid Confidence live slider + gold/silver alerts",
      "Position-specific gauges + weekly matchup map",
    ],
    product: "analytics_draft_access",
    requiresDraft: true,
  },
  {
    key: "analytics_monthly",
    title: "In-Season PGF Analytics",
    price: "$6.99",
    priceUnit: "/ mo",
    priceNote: "Pregame Fantasy First Year Launch Price · through Week 17",
    tagline: "Dashboard + in-app PGF Analytics through fantasy championship week.",
    items: [
      "Full PGF Analytics tab on every player",
      "Pre-draft + in-season dashboard surface",
      "Cancel anytime",
    ],
    product: "analytics_monthly",
    featured: true,
  },
];

async function startCheckout({ product, navigate, setBusy, setErr }) {
  setBusy(product);
  setErr(null);
  try {
    const token = localStorage.getItem("token");
    if (!token) { navigate("/signup?next=/pricing"); return; }
    const r = await fetch(`${API}/api/stripe/create-checkout-session`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      body: JSON.stringify({ product, origin_url: window.location.origin }),
    });
    if (!r.ok) {
      const d = await r.json().catch(() => ({}));
      throw new Error(d.detail || `HTTP ${r.status}`);
    }
    const { url } = await r.json();
    window.location.href = url;
  } catch (e) {
    setErr(e.message || "Checkout failed");
    setBusy(null);
  }
}

export default function PricingPage() {
  const navigate = useNavigate();
  const [busy, setBusy] = useState(null);
  const [err, setErr] = useState(null);
  const authed = !!localStorage.getItem("token");

  return (
    <div data-testid="pricing-page" className="min-h-screen px-4 py-12" style={{ background: "#0a0a0a", color: "#F5F5F5" }}>
      <div className="max-w-6xl mx-auto">
        <header className="mb-10 text-center">
          <p className="font-display font-black text-xs uppercase tracking-widest text-[#FF6B00]">Pricing</p>
          <h1 className="font-display font-black text-4xl sm:text-5xl mt-2">Pay for the night, the season, or the system.</h1>
          <p className="font-body text-sm mt-3 opacity-80 max-w-2xl mx-auto">
            Snake drafts are free. Auction + PGF Analytics are paid surfaces - pick the cadence that fits how you play.
          </p>
        </header>

        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {TIERS.map((t) => {
            const displayPrice = t.getPrice ? t.getPrice() : t.price;
            return (
              <div
                key={t.key}
                data-testid={`pricing-tier-${t.key}`}
                className={
                  "rounded-xl p-5 border flex flex-col " +
                  (t.featured
                    ? "border-[#FF6B00] bg-[#FF6B00]/5 shadow-[0_0_24px_-4px_rgba(255,107,0,0.5)]"
                    : "border-white/10 bg-white/[0.02]")
                }
              >
                {t.featured && (
                  <span className="font-display font-black text-[10px] uppercase tracking-widest text-[#FF6B00] mb-2">Most popular</span>
                )}
                <h2 className="font-display font-black text-lg leading-tight">{t.title}</h2>
                <div className="mt-3 flex items-baseline gap-1">
                  <span className="font-display font-black text-3xl">{displayPrice}</span>
                  {t.priceUnit && <span className="font-body text-sm opacity-70">{t.priceUnit}</span>}
                </div>
                {t.priceNote && (
                  <p className="font-body text-[11px] opacity-70 mt-1">{t.priceNote}</p>
                )}
                <p className="font-body text-sm mt-2 opacity-90">{t.tagline}</p>
                <ul className="mt-4 space-y-1.5 text-sm font-body opacity-90 flex-1">
                  {t.items.map((it, i) => (
                    <li key={i} className="flex gap-2"><span className="text-[#FF6B00]">▸</span><span>{it}</span></li>
                  ))}
                </ul>
                <div className="mt-5">
                  {!t.product ? (
                    <button
                      data-testid={`pricing-cta-${t.key}`}
                      onClick={() => navigate(authed ? "/dashboard" : "/signup")}
                      className="w-full px-4 py-2 rounded-md bg-white text-black hover:bg-white/90 font-display font-black text-sm uppercase tracking-wider transition-colors"
                    >
                      {authed ? "Open Dashboard" : "Sign up"}
                    </button>
                  ) : t.requiresLeague || t.requiresDraft ? (
                    <button
                      data-testid={`pricing-cta-${t.key}`}
                      onClick={() => navigate(authed ? "/dashboard" : "/signup?next=/pricing")}
                      className="w-full px-4 py-2 rounded-md border border-[#FF6B00] hover:bg-[#FF6B00]/15 font-display font-black text-sm uppercase tracking-wider text-[#FF6B00] transition-colors"
                    >
                      {t.requiresLeague ? "Buy from league creation" : "Buy from draft room"}
                    </button>
                  ) : (
                    <button
                      data-testid={`pricing-cta-${t.key}`}
                      disabled={!!busy}
                      onClick={() => startCheckout({ product: t.product, navigate, setBusy, setErr })}
                      className={
                        "w-full px-4 py-2 rounded-md disabled:opacity-50 font-display font-black text-sm uppercase tracking-wider transition-colors " +
                        (t.featured ? "bg-[#FF6B00] hover:bg-[#FF8533] text-white shadow-lg" : "bg-white text-black hover:bg-white/90")
                      }
                    >
                      {busy === t.product ? "Redirecting…" : (authed ? "Buy" : "Sign up to buy")}
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {err && (
          <p data-testid="pricing-error" className="font-body text-sm text-red-400 mt-6 text-center">{err}</p>
        )}

        <footer className="mt-10 text-center font-body text-xs opacity-60 max-w-2xl mx-auto">
          Auction Draft Pass standard rate ($39.99) takes effect Aug 1, 2026. Extended pass ($44.99) replaces the base pass for unlimited leagues.
        </footer>
      </div>
    </div>
  );
}
